import {
  MapboxFeature,
  MapDrawerEntityForPoi,
} from 'features/mapping/mapping.types';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import { SimlePolygonLayer } from './SimpleStylePolygonLayer';
import { SimplePointLayer } from './SimpleStylePointLayer';
import {
  ExternalFeatureLayerProps,
  ExternalFeatureProperties,
} from './ExternalFeatureLayer.types';
import { SimpleLineLayer } from './SimpleStyleLineLayer';
import {
  dedupeExternalFeatures,
  getExternalFeatureDrawerEntity,
} from './ExternalFeatureLayer.utils';
import { EmbeddedStylePolygonLayer } from './EmbeddedStylePolygonLayer';
import { EmbeddedStyleLineLayer } from './EmbeddedStyleLineLayer';
import { EmbeddedStylePointLayer } from './EmbeddedPointLayer';

const REFRESH_INTERVAL_SECONDS = 3600 * 3; // 3 hours

const makeLayerComponents = (
  props: ExternalFeatureLayerProps,
): JSX.Element | null => {
  const { featureLayer } = props;
  const { displayStyleData, geomType } = featureLayer;

  if (displayStyleData.displayStyleType === 'embedded') {
    return (
      <>
        {geomType === 'point' && <EmbeddedStylePointLayer {...props} />}
        {geomType === 'polygon' && <EmbeddedStylePolygonLayer {...props} />}
        {geomType === 'line' && <EmbeddedStyleLineLayer {...props} />}
      </>
    );
  }
  if (displayStyleData.displayStyleType === 'simple_single_color') {
    return (
      <>
        {geomType === 'point' && <SimplePointLayer {...props} />}
        {geomType === 'polygon' && <SimlePolygonLayer {...props} />}
        {geomType === 'line' && <SimpleLineLayer {...props} />}
      </>
    );
  }

  return null;
};

/**
 * Returns one drawer entity per clicked feature so overlapping polygons each
 * become their own stacked-navigation entry, de-duplicating features that share
 * a feature id.
 */
export const externalFeatureLayerClickHandler = (
  features: MapboxFeature<ExternalFeatureProperties>[],
): MapDrawerEntityForPoi[] =>
  dedupeExternalFeatures(features).map(getExternalFeatureDrawerEntity);

export const ExternalFeatureLayer = (
  props: ExternalFeatureLayerProps,
): JSX.Element | null => {
  const {
    visible,
    sourceId,
    featureLayer: { maptilesUrl },
  } = props;

  const layers = makeLayerComponents(props);
  return (
    <VisibilityToggledSource
      visible={visible}
      url={maptilesUrl}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
      id={sourceId}
    >
      {layers}
    </VisibilityToggledSource>
  );
};
